import { Button, Checkbox, Dropdown, DropdownItem, HFlow, Icon, TextField, useLocale, useStyles } from 'bold-ui'
import matchSorter, { rankings } from 'match-sorter'
import React, { useEffect, useState } from 'react'
import { useDrag } from 'react-dnd'

import { DraggableRow } from './DraggableRow'
import { DraggableWrapper } from './DraggableWrapper'
import { FilterDraggableProps } from './FilterDraggable'
import { draggableCreateStyles } from './style'
import { ActualQuantity } from './types/ActualQuantity'
import { useDraggableKeyNavigation } from './useDraggableNavigation'
import { getQuantityValue } from './util'

export function InternalFilterDraggable<T extends object, TOrigin = string>(props: FilterDraggableProps<T, TOrigin>) {
  const {
    name,
    origin,
    value,
    filterItems,
    selectedItems,
    onDragEnd,
    onFilterUpdate,
    formatter,
    onKeyNav,
    type,
  } = props

  const [searchedFilterSet, setSearchedFilterSet] = useState<Array<string>>(filterItems)

  const [open, setOpen] = useState(false)

  const [all, setAll] = useState<ActualQuantity>(getQuantityValue(selectedItems, filterItems))

  const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>()

  const { classes } = useStyles(draggableCreateStyles)

  const [{ isDragging }, drag] = useDrag({
    item: { type: type, name: name, origin },
    end: (_item, monitor) => {
      if (monitor.getDropResult()['result']) onDragEnd()
    },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  })

  const locale = useLocale()

  useEffect(() => {
    setAll(getQuantityValue(selectedItems, filterItems))
  }, [selectedItems, filterItems])

  if (filterItems.length < 1) {
    throw new Error(`The filterItems must have at least one element`)
  }

  selectedItems.forEach((value) => {
    if (!filterItems.includes(value)) {
      throw new Error(`The value '${value}' of selectedItems doesn't exist in filterItems[${filterItems.toString()}]`)
    }
  })

  const handleClick = () => (open ? handleClose() : setOpen(true))

  const handleClose = () => {
    setOpen(false)
    setSearchedFilterSet(filterItems)
  }

  const handleSelect = (element: string) => () => {
    const newSelectedItems = new Set(selectedItems)

    if (newSelectedItems.has(element)) {
      newSelectedItems.delete(element)
    } else {
      newSelectedItems.add(element)
    }

    onFilterUpdate(name, newSelectedItems)
    setAll(getQuantityValue(newSelectedItems, filterItems))
  }

  const { handleKeyDown } = useDraggableKeyNavigation(onDragEnd, origin, onKeyNav)

  const handleSearch = () => (event: any) =>
    setSearchedFilterSet(matchSorter(filterItems, event.currentTarget.value, { threshold: rankings.STARTS_WITH }))

  const handleSelectAll = () => {
    if (all === ActualQuantity.ALL) {
      setAll(ActualQuantity.NONE)
      onFilterUpdate(name, new Set<string>(new Set<string>()))
    } else {
      setAll(ActualQuantity.ALL)
      onFilterUpdate(name, new Set<string>(filterItems))
    }
  }

  return (
    <DraggableWrapper drag={drag} isDragging={isDragging}>
      <Button
        style={classes.button}
        innerRef={setButtonRef}
        onClick={handleClick}
        onKeyDown={handleKeyDown(name)}
        size='small'
        kind='primary'
        skin='ghost'
      >
        <HFlow hSpacing={0.5}>
          <Icon icon='dragdrop' />
          {value}
          {open ? <Icon icon='angleUp' /> : <Icon icon='angleDown' />}
        </HFlow>
      </Button>
      <Dropdown
        anchorRef={buttonRef}
        open={open}
        autoclose={false}
        onClose={handleClose}
        popperProps={{ placement: 'bottom' }}
        style={classes.dropdown}
      >
        <div key='dropDownArea' className={classes.dropdownArea} onBlur={(e) => e.stopPropagation()}>
          <DropdownItem className={classes.noOutline}>
            <div className={classes.search}>
              <TextField
                name='iconized'
                id='iconized'
                placeholder={locale.draggable.search}
                icon='zoomOutline'
                onChange={handleSearch()}
              />
            </div>
          </DropdownItem>

          {searchedFilterSet.length === filterItems.length && (
            <DropdownItem
              key={locale.draggable.all}
              className={classes.dropdownItem}
              aria-checked={all === ActualQuantity.ALL ? 'true' : all === ActualQuantity.NONE ? 'false' : 'mixed'}
            >
              <Checkbox
                label={locale.draggable.allItems}
                onChange={handleSelectAll}
                checked={all === ActualQuantity.ALL}
                indeterminate={all === ActualQuantity.ONE_OR_MORE}
              />
            </DropdownItem>
          )}

          {searchedFilterSet.map((value) => (
            <DraggableRow<T>
              key={value}
              value={value}
              name={name}
              selected={selectedItems.has(value)}
              handleSelect={handleSelect}
              formatter={formatter}
            />
          ))}
        </div>
      </Dropdown>
    </DraggableWrapper>
  )
}
